import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check, RotateCcw, Camera, FileCheck } from 'lucide-react';
import { RitualIcon } from '@/components/ui/RitualIcon';
import { cn } from '@/lib/utils';

const RitualIconWrapper = () => <RitualIcon size="md" />;

const demoSteps = [
  {
    task: 'Approve equipment order',
    result: 'Approved, PO sent',
    icon: FileCheck,
    direction: 'right' as const,
    color: 'success',
  },
  {
    task: 'Review daily checklist',
    result: 'Completed, logged',
    icon: Check,
    direction: 'right' as const,
    color: 'success',
  },
  {
    task: 'Skip — waiting on vendor',
    result: 'Rescheduled for Thursday',
    icon: RotateCcw,
    direction: 'left' as const,
    color: 'muted',
  },
  {
    task: 'Upload photo proof',
    result: 'Camera opens inline',
    icon: Camera,
    direction: 'right' as const,
    color: 'success',
  },
  {
    task: 'Agent suggests: new SOP',
    result: 'Auto-generated in 2 min',
    icon: RitualIconWrapper,
    direction: 'right' as const,
    color: 'primary',
  },
];

export function DemoStrip() {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  return (
    <div ref={containerRef} className="relative">
      {/* Horizontal scrolling container */}
      <div className="overflow-x-auto scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6">
        <div className="flex gap-4 sm:gap-6 pb-4 min-w-max">
          {demoSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{
                  delay: index * 0.15,
                  duration: 0.4,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                className="flex-shrink-0 w-64 sm:w-72"
              >
                <div className="bg-card rounded-xl border border-border p-5 shadow-card relative overflow-hidden group hover:shadow-lg transition-shadow">
                  {/* Step number */}
                  <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
                    {index + 1}
                  </div>

                  {/* Swipe direction indicator */}
                  <div className="mb-4 flex items-center gap-2">
                    <motion.div
                      className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center",
                        step.direction === 'right' ? 'bg-success/20' : 'bg-muted'
                      )}
                      initial={{ scale: 0 }}
                      animate={isInView ? { scale: 1 } : {}}
                      transition={{ delay: index * 0.15 + 0.2, type: 'spring' }}
                    >
                      {step.direction === 'right' ? (
                        <Check className="w-4 h-4 text-success" />
                      ) : (
                        <RotateCcw className="w-4 h-4 text-muted-foreground" />
                      )}
                    </motion.div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Swipe {step.direction}
                    </span>
                  </div>

                  {/* Task */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                      step.color === 'success' ? 'bg-success/10' :
                      step.color === 'primary' ? 'bg-primary/10' : 'bg-muted'
                    )}>
                      <Icon className={cn(
                        "w-5 h-5",
                        step.color === 'success' ? 'text-success' :
                        step.color === 'primary' ? 'text-primary' : 'text-muted-foreground'
                      )} />
                    </div>
                    <div>
                      <p className="font-medium text-foreground text-sm leading-tight">
                        {step.task}
                      </p>
                    </div>
                  </div>

                  {/* Result */}
                  <div className={cn(
                    "rounded-lg p-3 text-sm",
                    step.color === 'success' ? 'bg-success/10 text-success' :
                    step.color === 'primary' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                  )}>
                    → {step.result}
                  </div>

                  {/* Arc decoration */}
                  <svg
                    className="absolute -bottom-6 -right-6 w-24 h-24 opacity-10"
                    viewBox="0 0 100 100"
                  >
                    <path
                      d="M 20 80 Q 50 30 80 80"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      className={cn(
                        step.color === 'success' ? 'text-success' :
                        step.color === 'primary' ? 'text-primary' : 'text-muted-foreground'
                      )}
                    />
                  </svg>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Scroll hint for mobile */}
      <div className="flex justify-center mt-4 sm:hidden">
        <div className="flex gap-1">
          {demoSteps.map((_, i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30" />
          ))}
        </div>
      </div>
    </div>
  );
}
